// Menu look options (owner 2026-11-02: "the previous font and the menu setup … was much easier to see" / "give me more options
// to manipulate it, change it, configure it"). Saved in the profile (save.ts v6 `menu`), so Export save carries them.
// Pure: no DOM. main.ts applies them as body classes / CSS variables (src/style.css › MENU LOOK).

/** classic = the pre-Bright 'Rajdhani' type (the default again); rounded = 'Baloo 2' (the Bright look); clean / bold =
 *  'Atkinson Hyperlegible Next' at regular or heavy body weights */
export type MenuFont = 'classic' | 'rounded' | 'clean' | 'bold';
/** text outline / shadow on menu text: off, a soft drop shadow, or the menu style's own full outline / glow */
export type TextOutline = 'off' | 'soft' | 'strong';
/** comfortable = roomy rows and big tap targets; compact = tighter spacing so more fits on screen */
export type MenuDensity = 'comfortable' | 'compact';

export interface MenuPrefs {
  font: MenuFont;
  outline: TextOutline;
  density: MenuDensity;
  /** opacity of menu panels and the dim behind them, percent (70..100, steps of 5): lower shows more of the game */
  panelOpacity: number;
}

export const MENU_FONTS: readonly MenuFont[] = ['classic', 'rounded', 'clean', 'bold'];
export const TEXT_OUTLINES: readonly TextOutline[] = ['off', 'soft', 'strong'];
export const MENU_DENSITIES: readonly MenuDensity[] = ['comfortable', 'compact'];
export const PANEL_STEPS = [70, 75, 80, 85, 90, 95, 100] as const;

export function defaultMenuPrefs(): MenuPrefs {
  return { font: 'classic', outline: 'soft', density: 'comfortable', panelOpacity: 100 };
}

/** "Classic menus" (Settings › Display): the pre-redesign look in one step — Rajdhani type with the original glow, roomy
 *  rows and the dark Midnight menu style. Panel opacity is left as it is. */
export const CLASSIC_PRESET: { font: MenuFont; outline: TextOutline; density: MenuDensity; theme: 'midnight' } = { font: 'classic', outline: 'strong', density: 'comfortable', theme: 'midnight' };

const pick = <T extends string>(v: unknown, all: readonly T[], d: T): T => (all.includes(v as T) ? (v as T) : d);
const snapPct = (v: unknown, d: number): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  if (!Number.isFinite(n)) return d;
  const c = Math.max(PANEL_STEPS[0], Math.min(PANEL_STEPS[PANEL_STEPS.length - 1], n));
  return PANEL_STEPS.reduce<number>((a, b) => (Math.abs(b - c) < Math.abs(a - c) ? b : a), PANEL_STEPS[0]);
};

/** Upgrade any stored / imported shape (v5 and older saves have none: the defaults). Never throws. */
export function migrateMenuPrefs(raw: unknown): MenuPrefs {
  const p = defaultMenuPrefs();
  if (!raw || typeof raw !== 'object') return p;
  const r = raw as Partial<Record<keyof MenuPrefs, unknown>>;
  p.font = pick(r.font, MENU_FONTS, p.font);
  p.outline = pick(r.outline, TEXT_OUTLINES, p.outline);
  p.density = pick(r.density, MENU_DENSITIES, p.density);
  p.panelOpacity = snapPct(r.panelOpacity, p.panelOpacity);
  return p;
}

/** the body classes for these prefs (main.ts toggles every class in ALL_MENU_CLASSES, then adds these) */
export function menuClasses(p: MenuPrefs): string[] {
  return [`mf-${p.font}`, `ol-${p.outline}`, `md-${p.density === 'compact' ? 'compact' : 'comfy'}`];
}
export const ALL_MENU_CLASSES: readonly string[] = [...MENU_FONTS.map((f) => `mf-${f}`), ...TEXT_OUTLINES.map((o) => `ol-${o}`), 'md-comfy', 'md-compact'];
